import { useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import clsx from 'clsx';
import type { Message, StreamElement, ThinkingGroup as ThinkingGroupType, SubAgentBlock as SubAgentBlockType } from '../../types/chat';
import { ThumbsUp, ThumbsDown, Download } from 'lucide-react';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import { ThinkingGroup } from './ThinkingGroup';
import { SubAgentBlock } from './SubAgentBlock';
import { VideoPlayer, extractClipId, extractVideoUrl } from './VideoPlayer';
import { submitFeedback } from '../../api/feedback';
import { extractSuggestions } from './suggestionUtils';

// Import highlight.js styles
import 'highlight.js/styles/github-dark.css';

// Regex to match video embed syntax: {{video:clip_id}}
const VIDEO_EMBED_REGEX = /\{\{video:([^}]+)\}\}/g;

/**
 * Check if content contains video embeds.
 */
function hasVideoEmbeds(content: string): boolean {
  return VIDEO_EMBED_REGEX.test(content);
}

/**
 * Split content into parts around video embeds.
 * Returns array of { type: 'text', content: string } | { type: 'video', clipId: string }
 */
function splitVideoEmbeds(content: string): Array<{ type: 'text'; content: string } | { type: 'video'; clipId: string }> {
  const parts: Array<{ type: 'text'; content: string } | { type: 'video'; clipId: string }> = [];
  let lastIndex = 0;

  // Reset regex state
  VIDEO_EMBED_REGEX.lastIndex = 0;

  let match;
  while ((match = VIDEO_EMBED_REGEX.exec(content)) !== null) {
    // Add text before this match
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: content.slice(lastIndex, match.index) });
    }
    // Add video embed
    parts.push({ type: 'video', clipId: match[1] });
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last match
  if (lastIndex < content.length) {
    parts.push({ type: 'text', content: content.slice(lastIndex) });
  }

  return parts;
}

// Grouped element type - either a ThinkingGroup or a SubAgentBlock
type GroupedElement =
  | { type: 'thinking_group'; group: ThinkingGroupType }
  | { type: 'subagent'; block: SubAgentBlockType };

// Convert flat stream elements into grouped structure
function groupStreamElements(elements: StreamElement[]): GroupedElement[] {
  const groups: GroupedElement[] = [];
  let currentGroup: ThinkingGroupType | null = null;

  for (const el of elements) {
    if (el.type === 'thinking') {
      // Start a new group with this thinking
      currentGroup = { id: el.id, thinking: el.content, tools: [] };
      groups.push({ type: 'thinking_group', group: currentGroup });
    } else if (el.type === 'tool') {
      if (currentGroup) {
        // Add tool to current group
        currentGroup.tools.push(el.toolCall);
      } else {
        // Tool without preceding thinking - create empty group
        currentGroup = { id: el.toolCall.id, thinking: '', tools: [el.toolCall] };
        groups.push({ type: 'thinking_group', group: currentGroup });
      }
    } else if (el.type === 'subagent') {
      // SubAgent blocks are standalone - finalize current group and add subagent
      currentGroup = null;  // Reset so next elements start fresh
      groups.push({ type: 'subagent', block: el.subAgentBlock });
    }
  }

  return groups;
}

// Split content into thinking (before formatted response) and the actual response.
// The backend separates thinking from response via SSE event types (thinking vs text),
// so msg.content should already be the clean response. No client-side re-splitting needed.
function splitThinkingFromContent(content: string): { thinking: string; response: string } {
  return { thinking: '', response: content || '' };
}

interface ChatMessageProps {
  message: Message;
  userPrompt?: string;
  sessionId?: string;
}

export function ChatMessage({ message, userPrompt, sessionId }: ChatMessageProps) {
  const isUser = message.role === 'user';
  const hasContent = message.content && message.content.length > 0;

  // Group stream elements into grouped elements (thinking groups + subagent blocks)
  const groupedElements = useMemo(() => {
    if (!message.streamElements?.length) return [];
    return groupStreamElements(message.streamElements);
  }, [message.streamElements]);

  // Filter out last thinking group if it has no tools (that's the final response content)
  // Thinking blocks WITH tools should remain visible to show the reasoning process
  const filteredElements = useMemo(() => {
    if (groupedElements.length === 0) return [];
    const lastElement = groupedElements[groupedElements.length - 1];
    // If last element is a thinking group with no tools and we have message content, hide it
    // (it's the final response, displayed via message.content with markdown)
    if (lastElement.type === 'thinking_group' && lastElement.group.tools.length === 0 && hasContent) {
      return groupedElements.slice(0, -1);
    }
    return groupedElements;
  }, [groupedElements, hasContent]);

  // Split message content into thinking and formatted response
  const { thinking: contentThinking, response: contentResponse } = useMemo(() => {
    if (!message.content) return { thinking: '', response: '' };
    return splitThinkingFromContent(message.content);
  }, [message.content]);

  // Extract suggestion chips from the response content
  const { cleanedContent: displayContent } = useMemo(() => {
    if (!contentResponse) return { cleanedContent: '', suggestions: [] };
    return extractSuggestions(contentResponse);
  }, [contentResponse]);

  // Build clipId → videoUrl lookup from tool results in this message.
  // Video embeds in the final response ({{video:clip_id}}) only carry the clip ID,
  // so we resolve the S3 presigned URL from the tool result that produced it.
  const videoUrlMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!message.streamElements) return map;

    for (const el of message.streamElements) {
      if (el.type === 'tool' && el.toolCall.name === 'extract_video_clip' && el.toolCall.result) {
        const clipId = extractClipId(el.toolCall.result);
        const videoUrl = extractVideoUrl(el.toolCall.result);
        if (clipId && videoUrl) map.set(clipId, videoUrl);
      }
      if (el.type === 'subagent') {
        for (const phase of el.subAgentBlock.phases) {
          for (const tool of phase.tools) {
            if (tool.name === 'extract_video_clip' && tool.result) {
              const clipId = extractClipId(tool.result);
              const videoUrl = extractVideoUrl(tool.result);
              if (clipId && videoUrl) map.set(clipId, videoUrl);
            }
          }
        }
      }
    }

    return map;
  }, [message.streamElements]);

  const hasGroupedElements = filteredElements.length > 0;
  const hasContentThinking = contentThinking.length > 0;
  const hasResponse = displayContent.length > 0;
  const isStreamingEmpty = message.isStreaming && !hasContent && !hasGroupedElements;

  const responseRef = useRef<HTMLDivElement>(null);
  const [feedbackGiven, setFeedbackGiven] = useState<'positive' | 'negative' | null>(null);
  const showFeedback = !isUser && hasResponse && !message.isStreaming;

  const handleFeedback = (type: 'positive' | 'negative') => {
    setFeedbackGiven(type);
    if (sessionId) {
      submitFeedback({
        feedback_type: type,
        session_id: sessionId,
        prompt: userPrompt || '',
      });
    }
  };

  const exportToPdf = async () => {
    if (!responseRef.current) return;

    const el = responseRef.current;
    const dataUrl = await toPng(el, {
      pixelRatio: 2,
      height: el.scrollHeight + 20,
      width: el.scrollWidth,
      style: { paddingBottom: '20px' },
    });

    const img = new Image();
    img.src = dataUrl;
    await new Promise<void>((resolve) => { img.onload = () => resolve(); });

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const margin = 10;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const contentWidth = pageWidth - 2 * margin;
    const contentMaxHeight = pageHeight - 2 * margin;

    const scaledHeight = (contentWidth / img.width) * img.height;

    if (scaledHeight <= contentMaxHeight) {
      pdf.addImage(dataUrl, 'PNG', margin, margin, contentWidth, scaledHeight);
    } else {
      // Split into pages by slicing the source image
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      const pageImgHeight = (contentMaxHeight / scaledHeight) * img.height;
      let sourceY = 0;
      let remaining = img.height;
      let page = 0;

      while (remaining > 0) {
        const sliceH = Math.min(pageImgHeight, remaining);
        canvas.width = img.width;
        canvas.height = sliceH;
        ctx.drawImage(img, 0, sourceY, img.width, sliceH, 0, 0, img.width, sliceH);

        const sliceUrl = canvas.toDataURL('image/png');
        const sliceScaled = (contentWidth / img.width) * sliceH;

        if (page > 0) pdf.addPage();
        pdf.addImage(sliceUrl, 'PNG', margin, margin, contentWidth, sliceScaled);

        sourceY += sliceH;
        remaining -= sliceH;
        page++;
      }
    }

    pdf.save(`hudl-coach-export-${message.id}.pdf`);
  };

  return (
    <div
      className={clsx(
        'flex w-full',
        isUser ? 'justify-end' : 'justify-start'
      )}
    >
      <div
        className={clsx(
          'rounded-2xl',
          isUser
            ? 'max-w-[85%] bg-gradient-to-br from-orange-500 to-orange-600 text-white px-5 py-3 shadow-md'
            : 'w-full bg-white text-gray-900 shadow-lg border border-gray-100 px-5 py-4'
        )}
      >
        {/* Grouped elements (thinking groups + subagent blocks) */}
        {!isUser && hasGroupedElements && (
          <div className="space-y-2 mb-2">
            {filteredElements.map((element, idx) => {
              if (element.type === 'thinking_group') {
                return (
                  <ThinkingGroup
                    key={element.group.id}
                    group={element.group}
                    isStreaming={message.isStreaming && idx === filteredElements.length - 1 && !hasContent}
                  />
                );
              }
              if (element.type === 'subagent') {
                return (
                  <SubAgentBlock
                    key={element.block.id}
                    block={element.block}
                  />
                );
              }
              return null;
            })}
          </div>
        )}

        {/* Thinking extracted from final content (before markdown formatting) */}
        {!isUser && hasContentThinking && (
          <div className={clsx(hasGroupedElements && 'mt-2')}>
            <ThinkingGroup
              group={{ id: 'content-thinking', thinking: contentThinking, tools: [] }}
              isStreaming={false}
            />
          </div>
        )}

        {/* Message content (formatted response only) */}
        {hasResponse ? (
          <div
            ref={responseRef}
            className={clsx(
              'prose prose-sm max-w-none',
              isUser && 'prose-invert',
              !isUser && (hasGroupedElements || hasContentThinking) && 'mt-3 pt-3 border-t border-gray-100'
            )}
          >
            {/* Check for video embeds and render them inline with markdown */}
            {hasVideoEmbeds(displayContent) ? (
              // Split content and render videos inline
              <>
                {splitVideoEmbeds(displayContent).map((part, idx) => {
                  if (part.type === 'video') {
                    return <VideoPlayer key={`video-${idx}`} clipId={part.clipId} videoUrl={videoUrlMap.get(part.clipId)} />;
                  }
                  // Render markdown for text parts
                  return (
                    <ReactMarkdown
                      key={`text-${idx}`}
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeHighlight]}
                      components={{
                        pre: ({ children }) => (
                          <pre className="bg-gray-800 rounded-xl overflow-x-auto p-4 my-3 text-sm">
                            {children}
                          </pre>
                        ),
                        code: ({ className, children, ...props }) => {
                          const isInline = !className;
                          return isInline ? (
                            <code
                              className={clsx(
                                'px-1.5 py-0.5 rounded text-sm font-mono',
                                isUser ? 'bg-orange-400/30' : 'bg-gray-100 text-gray-800'
                              )}
                              {...props}
                            >
                              {children}
                            </code>
                          ) : (
                            <code className={className} {...props}>
                              {children}
                            </code>
                          );
                        },
                        table: ({ children }) => (
                          <div className="overflow-x-auto my-3 rounded-lg border border-gray-200">
                            <table className="min-w-full divide-y divide-gray-200">
                              {children}
                            </table>
                          </div>
                        ),
                        thead: ({ children }) => (
                          <thead className="bg-gray-50">{children}</thead>
                        ),
                        th: ({ children }) => (
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            {children}
                          </th>
                        ),
                        td: ({ children }) => (
                          <td className="px-4 py-3 text-sm border-t border-gray-100">{children}</td>
                        ),
                        ul: ({ children }) => (
                          <ul className="list-disc pl-5 space-y-1 my-2">{children}</ul>
                        ),
                        ol: ({ children }) => (
                          <ol className="list-decimal pl-5 space-y-1 my-2">{children}</ol>
                        ),
                        li: ({ children }) => (
                          <li className="[&>p]:inline [&>p]:m-0">{children}</li>
                        ),
                        p: ({ children }) => (
                          <p className="my-2 leading-relaxed">{children}</p>
                        ),
                        h1: ({ children }) => (
                          <h1 className="text-xl font-bold mt-4 mb-2">{children}</h1>
                        ),
                        h2: ({ children }) => (
                          <h2 className="text-lg font-bold mt-4 mb-2">{children}</h2>
                        ),
                        h3: ({ children }) => (
                          <h3 className="text-base font-semibold mt-3 mb-2">{children}</h3>
                        ),
                        blockquote: ({ children }) => (
                          <blockquote className="border-l-4 border-gray-300 pl-4 my-3 italic text-gray-600">
                            {children}
                          </blockquote>
                        ),
                        a: ({ href, children }) => (
                          <a href={href} className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                            {children}
                          </a>
                        ),
                      }}
                    >
                      {part.content}
                    </ReactMarkdown>
                  );
                })}
              </>
            ) : (
              // No video embeds - render markdown normally
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeHighlight]}
                components={{
                  pre: ({ children }) => (
                    <pre className="bg-gray-800 rounded-xl overflow-x-auto p-4 my-3 text-sm">
                      {children}
                    </pre>
                  ),
                  code: ({ className, children, ...props }) => {
                    const isInline = !className;
                    return isInline ? (
                      <code
                        className={clsx(
                          'px-1.5 py-0.5 rounded text-sm font-mono',
                          isUser ? 'bg-orange-400/30' : 'bg-gray-100 text-gray-800'
                        )}
                        {...props}
                      >
                        {children}
                      </code>
                    ) : (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    );
                  },
                  table: ({ children }) => (
                    <div className="overflow-x-auto my-3 rounded-lg border border-gray-200">
                      <table className="min-w-full divide-y divide-gray-200">
                        {children}
                      </table>
                    </div>
                  ),
                  thead: ({ children }) => (
                    <thead className="bg-gray-50">{children}</thead>
                  ),
                  th: ({ children }) => (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td className="px-4 py-3 text-sm border-t border-gray-100">{children}</td>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc pl-5 space-y-1 my-2">{children}</ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal pl-5 space-y-1 my-2">{children}</ol>
                  ),
                  li: ({ children }) => (
                    <li className="[&>p]:inline [&>p]:m-0">{children}</li>
                  ),
                  p: ({ children }) => (
                    <p className="my-2 leading-relaxed">{children}</p>
                  ),
                  h1: ({ children }) => (
                    <h1 className="text-xl font-bold mt-4 mb-2">{children}</h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="text-lg font-bold mt-4 mb-2">{children}</h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-base font-semibold mt-3 mb-2">{children}</h3>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-gray-300 pl-4 my-3 italic text-gray-600">
                      {children}
                    </blockquote>
                  ),
                  a: ({ href, children }) => (
                    <a href={href} className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                      {children}
                    </a>
                  ),
                }}
              >
                {displayContent}
              </ReactMarkdown>
            )}
            {message.isStreaming && (
              <span className="inline-block w-2 h-5 bg-gray-400 animate-pulse ml-0.5 rounded-sm" />
            )}
          </div>
        ) : isStreamingEmpty ? (
          // Loading state when nothing has arrived yet
          <div className="flex items-center gap-2 text-gray-500">
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <span className="text-sm">Thinking...</span>
          </div>
        ) : null}

        {/* Feedback thumbs + Export to PDF */}
        {showFeedback && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center">
            {feedbackGiven ? (
              <p className="text-xs text-gray-400">Thanks for your feedback!</p>
            ) : (
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400">Was this helpful?</span>
                <button
                  onClick={() => handleFeedback('positive')}
                  className="text-gray-400 hover:text-green-600 transition-colors"
                  title="Helpful"
                >
                  <ThumbsUp className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleFeedback('negative')}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                  title="Not helpful"
                >
                  <ThumbsDown className="w-4 h-4" />
                </button>
              </div>
            )}
            <button
              onClick={exportToPdf}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors"
              title="Export to PDF"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Export to PDF</span>
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
